'use client'

import type { ProposalOrder } from '@moment/horizon-data-access/proposal'

import { toError } from '@moment/observability/logger'
import { useCallback, useEffect, useMemo, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Dialog from '@/components/atomic/dialog'
import Input from '@/components/atomic/input'
import { BodySubtext, BodyXS } from '@/components/atomic/text'
import { useGetPriceToYieldQuery } from '@/legacy-api/analytics-slice'
import { useUpdateProposalOrderMutation } from '@/legacy-api/proposals-slice'
import { createLogger } from '@/lib/logger'
import { PROPOSAL_FORMATTING } from '@/portfolio/proposal-builder/formatting-constants'
import isValidInstrumentIdFormat from '@/utils/instrument'
import { formatMoney } from '@/utils/presentation/formatting'
import { calculateProportionalTaxEffectiveYtw } from '@/utils/proposals/calculate-yields'

const LOGGER = createLogger('components/molecules/proposal-edit-order-dialog')

import {
  DialogContentContainer,
  DialogFooterContainer,
  PriceRow,
} from './styles'

interface ProposalEditOrderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order: ProposalOrder | null
  proposalId: string
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ProposalEditOrderDialog({
  open,
  onOpenChange,
  order,
  proposalId,
}: ProposalEditOrderDialogProps) {
  const [limitPrice, setLimitPrice] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [updateOrder, { isLoading: isUpdating }] =
    useUpdateProposalOrderMutation()

  // Initialize limit price when order changes
  useEffect(() => {
    if (open && order?.limit_price) {
      setLimitPrice(
        order.limit_price.toFixed(PROPOSAL_FORMATTING.PRICE_DECIMALS),
      )
    } else if (!open) {
      setLimitPrice('')
    }
  }, [order, open])

  const handleClose = useCallback(() => {
    onOpenChange(false)
    setLimitPrice('')
    setErrorMessage('')
  }, [onOpenChange])

  const handleLimitPriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLimitPrice(e.target.value)
      // Clear error message when user starts typing
      if (errorMessage) {
        setErrorMessage('')
      }
    },
    [errorMessage],
  )

  const isValidPrice = useMemo(() => {
    const priceValue = parseFloat(limitPrice)
    return !Number.isNaN(priceValue) && priceValue > 0
  }, [limitPrice])

  const instrumentId = useMemo(() => order?.instrument.isin ?? '', [order])

  const priceForYield = useMemo(() => {
    const priceValue = parseFloat(limitPrice)
    return !Number.isNaN(priceValue) && priceValue > 0 ? priceValue : undefined
  }, [limitPrice])

  const {
    data: { yield_to_worst, yield_to_maturity } = {
      yield_to_worst: undefined,
      yield_to_maturity: undefined,
    },
  } = useGetPriceToYieldQuery(
    {
      instrumentId,
      price: priceForYield,
    },
    {
      skip:
        !instrumentId ||
        !isValidInstrumentIdFormat(instrumentId) ||
        !priceForYield,
    },
  )

  const calculatedTaxEffectiveYtw = useMemo(
    () =>
      calculateProportionalTaxEffectiveYtw(
        order?.tax_effective_limit_ytw,
        order?.limit_ytw,
        yield_to_worst,
      ),
    [order?.tax_effective_limit_ytw, order?.limit_ytw, yield_to_worst],
  )

  const handleSave = useCallback(async () => {
    if (!order || !limitPrice) return

    // Clear any existing error message
    setErrorMessage('')

    try {
      const priceValue = parseFloat(limitPrice)
      if (Number.isNaN(priceValue) || priceValue <= 0) {
        setErrorMessage('Please enter a valid price greater than 0')
        return
      }

      await updateOrder({
        proposal_id: proposalId,
        proposal_order_id: order.moment_order_id,
        limit_price: priceValue,
        limit_ytm: yield_to_maturity ?? null,
        limit_ytw: yield_to_worst ?? null,
        tax_effective_limit_ytw: calculatedTaxEffectiveYtw,
        post_tax_limit_ytw: yield_to_worst ?? null,
        instrument_id: order.instrument.isin,
        allocations: order.allocations,
      }).unwrap()

      handleClose()
    } catch (error) {
      LOGGER.error('Failed to update order', { error: toError(error) })
      setErrorMessage('Failed to update limit price. Please try again.')
    }
  }, [
    order,
    limitPrice,
    updateOrder,
    proposalId,
    handleClose,
    yield_to_maturity,
    yield_to_worst,
    calculatedTaxEffectiveYtw,
  ])

  if (!order) return null

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
      title="Edit Limit Price"
      dialogContentClassName="w-full max-w-[400px]"
      mainContent={
        <DialogContentContainer>
          <PriceRow>
            <BodySubtext>Current Limit Price</BodySubtext>
            <BodyXS>
              {formatMoney(order.limit_price, {
                numDigits: PROPOSAL_FORMATTING.PRICE_DECIMALS,
              })}
            </BodyXS>
          </PriceRow>

          <PriceRow>
            <BodySubtext>New Limit Price</BodySubtext>
            <Input
              value={limitPrice}
              onChange={handleLimitPriceChange}
              placeholder="Enter new limit price"
              type="number"
              step="0.01"
              min="0"
              size="small"
              error={!!errorMessage}
              errorMessage={errorMessage}
              slotProps={{
                side: 'left',
                label: '$',
                showSeparator: true,
              }}
            />
          </PriceRow>
        </DialogContentContainer>
      }
      footer={
        <DialogFooterContainer>
          <Button
            variant={ButtonVariant.Secondary}
            label="Cancel"
            onClick={handleClose}
            disabled={isUpdating}
          />
          <Button
            variant={ButtonVariant.Primary}
            label={isUpdating ? 'Saving...' : 'Save'}
            onClick={handleSave}
            disabled={!isValidPrice || isUpdating}
          />
        </DialogFooterContainer>
      }
    />
  )
}
